<script setup lang="ts">
/**
 * Admin → Settings: every registered setting grouped by category, edited
 * inline through its schema; environment-locked ones are read-only; keys
 * no setting uses any more are listed under "Unknown keys".
 *
 * Categories collapse (remembered per browser) and are listed in a table of
 * contents: a sticky column on wide screens, a chip row above the list
 * otherwise. Search and the "Modified" filter hide rows instead of removing
 * them, so unsaved edits survive a search.
 */
import { ArrowRight, ChevronRight, ChevronsDownUp, ChevronsUpDown, Settings, Trash2 } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { useEventListener } from "@vueuse/core";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { toast } from "vue-sonner";
import { putSetting, resetSetting, useSettings } from "@/api/admin";
import type { Setting, SettingPattern } from "@/api/contracts";
import SettingRow from "@/components/admin/SettingRow.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import FormError from "@/components/common/FormError.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import ScrollRow from "@/components/common/ScrollRow.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { confirm } from "@/composables/useConfirm";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { t } from "@/copy/strings";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Settings", requires: { adminArea: "settings" } } });

const copy = t.admin.settingsPage;
const queryClient = useQueryClient();
const settings = useSettings();
const q = useRouteQuery("q", "");
const show = ref<"modified" | undefined>(undefined);

const categoryTitle = (c: string) => copy.categories[c] ?? c;
const ORDER = Object.keys(copy.categories);
const rank = (c: string) => (ORDER.includes(c) ? ORDER.indexOf(c) : ORDER.length);
const sectionId = (c: string) => `cat-${c.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

function matches(needle: string, ...texts: (string | undefined)[]): boolean {
  return !needle || texts.some((x) => x?.toLowerCase().includes(needle));
}

/** Unsaved edits per setting key (reported by the rows). */
const dirty = ref<Record<string, boolean>>({});
const isModified = (s: Setting) => !s.isDefault || Boolean(s.invalid) || Boolean(dirty.value[s.key]);

/** Every category with all its settings (rows stay mounted; filters only hide them). */
const sections = computed(() => {
  const data = settings.data.value;
  if (!data) return [];
  const map = new Map<string, { items: Setting[]; patterns: SettingPattern[] }>();
  const slot = (c: string) => {
    if (!map.has(c)) map.set(c, { items: [], patterns: [] });
    return map.get(c)!;
  };
  for (const s of data.items) if (!s.orphaned) slot(s.category).items.push(s);
  for (const p of data.patterns) slot(p.category).patterns.push(p);
  return [...map.entries()].sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0])).map(([category, v]) => ({ category, ...v }));
});

const needle = computed(() => q.value.trim().toLowerCase());
const filtering = computed(() => Boolean(needle.value) || show.value === "modified");
const settingVisible = (s: Setting) =>
  matches(needle.value, s.title, s.key, s.description, categoryTitle(s.category)) && (show.value !== "modified" || isModified(s));
const patternVisible = (p: SettingPattern) =>
  show.value !== "modified" && matches(needle.value, p.title, p.keyPattern, p.description, categoryTitle(p.category));

const view = computed(() =>
  sections.value.map((s) => {
    const visible = s.items.filter(settingVisible).length + s.patterns.filter(patternVisible).length;
    return {
      ...s,
      id: sectionId(s.category),
      title: categoryTitle(s.category),
      total: s.items.length + s.patterns.length,
      visible,
      modified: s.items.filter((x) => !x.isDefault || x.invalid).length,
      unsaved: s.items.filter((x) => dirty.value[x.key]).length,
    };
  }),
);
const shownSections = computed(() => view.value.filter((s) => s.visible > 0));
const matchCount = computed(() => view.value.reduce((n, s) => n + s.visible, 0));
const modifiedTotal = computed(() => (settings.data.value?.items ?? []).filter((s) => !s.orphaned && (!s.isDefault || s.invalid)).length);
const unsavedTotal = computed(() => Object.values(dirty.value).filter(Boolean).length);
const showOptions = computed(() => [{ value: "modified" as const, label: copy.modifiedOnly, count: modifiedTotal.value }]);

const orphaned = computed(() => (settings.data.value?.items ?? []).filter((s) => s.orphaned && matches(needle.value, s.key)));

// --- collapsed categories (remembered in this browser; a filter shows every match).
// The first visit opens only the first category: the page starts as an overview.
const STORE = "smake.admin.settings.collapsed";
const collapsed = ref<Set<string>>(new Set());
let remembered = false;
try {
  const raw = localStorage.getItem(STORE);
  if (raw) {
    collapsed.value = new Set((JSON.parse(raw) as unknown[]).filter((x): x is string => typeof x === "string"));
    remembered = true;
  }
} catch {
  // storage unavailable: the first-visit default applies
}
watch(
  sections,
  (list) => {
    if (remembered || list.length === 0) return;
    remembered = true;
    collapsed.value = new Set(list.slice(1).map((s) => s.category));
  },
  { immediate: true },
);
function remember(v: Set<string>) {
  try {
    localStorage.setItem(STORE, JSON.stringify([...v]));
  } catch {
    // not remembered, still works
  }
}
const isOpen = (c: string) => filtering.value || !collapsed.value.has(c);
function toggle(c: string) {
  const next = new Set(collapsed.value);
  if (next.has(c)) next.delete(c);
  else next.add(c);
  collapsed.value = next;
  remember(next);
}
const allOpen = computed(() => sections.value.every((s) => !collapsed.value.has(s.category)));
function setAll(open: boolean) {
  collapsed.value = open ? new Set() : new Set(sections.value.map((s) => s.category));
  remember(collapsed.value);
}

// --- table of contents: jump to a category, highlight the one in view
const active = ref<string | null>(null);
const px = (v: string) => parseFloat(v) || 0;
/**
 * The category in view: the last one whose top has passed the line where a
 * jump puts it (the page's scroll padding plus the section's scroll margin,
 * read from the styles so the two cannot drift apart). At the bottom of the
 * page the categories below that line can never reach it; there the one
 * taking the most room in the viewport wins (a collapsed last category does
 * not steal the highlight from the open one that fills the screen).
 */
function spy() {
  const list = shownSections.value;
  if (!list.length) return (active.value = null);
  const pad = px(getComputedStyle(document.documentElement).scrollPaddingTop);
  const boxes = list.map((s) => {
    const el = document.getElementById(s.id);
    return el ? { rect: el.getBoundingClientRect(), line: pad + px(getComputedStyle(el).scrollMarginTop) + 8 } : null;
  });
  let index = 0;
  boxes.forEach((b, i) => {
    if (b && b.rect.top <= b.line) index = i;
  });
  const doc = document.documentElement;
  if (window.scrollY > 0 && window.innerHeight + window.scrollY >= doc.scrollHeight - 4) {
    let most = -1;
    for (let i = index; i < boxes.length; i++) {
      const b = boxes[i];
      if (!b) continue;
      const seen = Math.min(b.rect.bottom, window.innerHeight) - Math.max(b.rect.top, pad);
      if (seen >= most) [most, index] = [seen, i];
    }
  }
  active.value = list[index].category;
}

/**
 * After a click in the table of contents the clicked category stays
 * highlighted until its scroll comes to rest (no scroll event for a moment),
 * even where it cannot reach the top (the last categories).
 */
let holding = false;
let release = 0;
function hold(ms: number) {
  holding = true;
  clearTimeout(release);
  release = window.setTimeout(() => (holding = false), ms);
}
let frame = 0;
useEventListener(window, "scroll", () => {
  if (holding) return hold(150);
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(spy);
}, { passive: true });
onMounted(spy);
watch(shownSections, () =>
  nextTick(() => {
    if (!holding) spy();
  }),
);
onBeforeUnmount(() => {
  clearTimeout(release);
  cancelAnimationFrame(frame);
});

async function jump(category: string) {
  if (collapsed.value.has(category)) toggle(category);
  cancelAnimationFrame(frame);
  active.value = category;
  hold(400);
  await nextTick();
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  document.getElementById(sectionId(category))?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
}

// --- saving
const busyKey = ref<string | null>(null);
const errors = ref<Record<string, unknown>>({});

async function refresh() {
  await queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });
}

async function run(key: string, fn: () => Promise<unknown>, done: string) {
  busyKey.value = key;
  errors.value = { ...errors.value, [key]: null };
  try {
    await fn();
    toast.success(done);
    await refresh();
  } catch (err) {
    errors.value = { ...errors.value, [key]: err };
  } finally {
    busyKey.value = null;
  }
}

const save = (s: Setting, value: unknown) => run(s.key, () => putSetting(s.key, value), copy.saved(s.title));

async function reset(s: Setting) {
  if (!(await confirm({ title: copy.reset, description: copy.resetConfirm(s.key), confirmLabel: copy.reset }))) return;
  await run(s.key, () => resetSetting(s.key), copy.resetDone(s.title));
}

async function remove(s: Setting) {
  if (!(await confirm({ title: copy.deleteKey, description: copy.deleteConfirm(s.key), confirmLabel: copy.deleteKey, destructive: true }))) return;
  await run(s.key, () => resetSetting(s.key), copy.deleted(s.key));
}

function preview(v: unknown): string {
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return s.length > 90 ? `${s.slice(0, 89)}…` : s;
}
</script>

<template>
  <div>
    <PageHeader :crumbs="[{ label: t.admin.title, to: '/admin' }]" :title="t.admin.settings" :description="t.admin.settingsDescription" />

    <FilterBar v-model:search="q" :placeholder="copy.search" label="Filter settings" class="mb-5">
      <template v-if="filtering ? settings.data.value : sections.length" #end>
        <span v-if="filtering" class="text-xs text-muted-foreground tabular-nums" aria-live="polite">{{ copy.results(matchCount) }}</span>
        <Button v-else variant="ghost" size="sm" @click="setAll(!allOpen)">
          <ChevronsDownUp v-if="allOpen" /><ChevronsUpDown v-else /> {{ allOpen ? copy.collapseAll : copy.expandAll }}
        </Button>
      </template>
      <template #chips><FilterChips v-model="show" :options="showOptions" :label="copy.modifiedOnly" /></template>
    </FilterBar>
    <p v-if="unsavedTotal" role="status" class="mb-4 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-ink">{{ copy.unsavedHint(unsavedTotal) }}</p>

    <ListSkeleton v-if="settings.isPending.value" :rows="6" />
    <ErrorState v-else-if="settings.error.value" :error="settings.error.value" @retry="settings.refetch()" />
    <EmptyState v-else-if="sections.length === 0 && orphaned.length === 0" :icon="Settings" :title="copy.empty" />
    <div v-else class="grid min-w-0 gap-6 xl:grid-cols-[12rem_minmax(0,1fr)] xl:gap-8">
      <!-- Table of contents: a sticky column on wide screens -->
      <nav :aria-label="copy.categoriesLabel" class="hidden xl:block">
        <ul class="sticky top-20 flex flex-col gap-0.5">
          <li v-for="s in view" :key="s.category">
            <a
              :href="`#${s.id}`"
              :aria-current="active === s.category ? 'location' : undefined"
              :class="
                cn(
                  'relative flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors',
                  s.visible === 0 && 'pointer-events-none opacity-40',
                  active === s.category ? 'bg-state-selected font-medium text-foreground' : 'text-muted-foreground hover:bg-state-hover hover:text-foreground active:bg-state-press',
                )
              "
              @click.prevent="jump(s.category)"
            >
              <span v-if="active === s.category" class="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-primary" aria-hidden="true" />
              <span class="min-w-0 flex-1 truncate">{{ s.title }}</span>
              <span v-if="s.unsaved" class="size-1.5 shrink-0 rounded-full bg-warning" :title="copy.unsavedCount(s.unsaved)" aria-hidden="true" />
              <span class="shrink-0 text-xs text-muted-foreground tabular-nums">{{ filtering ? s.visible : s.total }}</span>
            </a>
          </li>
        </ul>
      </nav>

      <div class="flex min-w-0 flex-col gap-4">
        <!-- Table of contents: a chip row on smaller screens -->
        <nav
          :aria-label="copy.categoriesLabel"
          class="sticky top-14 z-20 -mx-4 -mt-2 border-b border-border/60 bg-background/85 px-4 py-2 backdrop-blur-xl sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 xl:hidden"
        >
          <ScrollRow as="ul" :active-key="active" class="-m-1 flex gap-1.5 p-1">
            <li v-for="s in shownSections" :key="s.category" class="shrink-0" :data-active="active === s.category || undefined">
              <a
                :href="`#${s.id}`"
                :aria-current="active === s.category ? 'location' : undefined"
                :class="
                  cn(
                    'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium whitespace-nowrap transition-colors pointer-coarse:h-9',
                    active === s.category ? 'border-primary/30 bg-primary/10 text-foreground' : 'border-input bg-control text-muted-foreground hover:bg-control-hover hover:text-foreground',
                  )
                "
                @click.prevent="jump(s.category)"
              >
                {{ s.title }}
                <span class="text-xs text-muted-foreground tabular-nums">{{ filtering ? s.visible : s.total }}</span>
              </a>
            </li>
          </ScrollRow>
        </nav>

        <EmptyState v-if="matchCount === 0 && orphaned.length === 0" :icon="Settings" :title="t.common.noResults" />

        <!-- A jump lands at the page's scroll padding (4.5rem, the top bar) plus this margin:
             just under the chip row, or level with the table of contents column (top-20). -->
        <section
          v-for="s in view"
          v-show="s.visible > 0"
          :id="s.id"
          :key="s.category"
          class="surface min-w-0 scroll-mt-12 overflow-hidden xl:scroll-mt-2"
          :aria-labelledby="`${s.id}-title`"
        >
          <h2 :id="`${s.id}-title`">
            <button
              type="button"
              class="flex w-full min-w-0 items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-state-hover active:bg-state-press disabled:cursor-default disabled:hover:bg-transparent sm:px-5"
              :aria-expanded="isOpen(s.category)"
              :aria-controls="`${s.id}-list`"
              :disabled="filtering"
              @click="toggle(s.category)"
            >
              <ChevronRight :class="cn('size-4 shrink-0 text-muted-foreground transition-transform duration-150', isOpen(s.category) && 'rotate-90')" aria-hidden="true" />
              <span class="card-title min-w-0 truncate">{{ s.title }}</span>
              <span class="shrink-0 text-xs font-normal text-muted-foreground tabular-nums">{{ copy.count(filtering ? s.visible : s.total) }}</span>
              <span class="ml-auto flex shrink-0 items-center gap-1.5">
                <Badge v-if="s.unsaved" variant="warning">{{ copy.unsavedCount(s.unsaved) }}</Badge>
                <Badge v-if="s.modified" variant="info" class="hidden sm:inline-flex">{{ copy.modifiedCount(s.modified) }}</Badge>
              </span>
            </button>
          </h2>
          <ul v-show="isOpen(s.category)" :id="`${s.id}-list`" class="min-w-0">
            <SettingRow
              v-for="item in s.items"
              v-show="settingVisible(item)"
              :key="item.key"
              class="border-t"
              :setting="item"
              :busy="busyKey === item.key"
              :error="errors[item.key]"
              @save="(v) => save(item, v)"
              @reset="reset(item)"
              @dirty="(d) => (dirty = { ...dirty, [item.key]: d })"
            />
            <li
              v-for="p in s.patterns"
              v-show="patternVisible(p)"
              :key="p.keyPattern"
              class="grid min-w-0 gap-x-8 gap-y-3 border-t px-4 py-4 sm:px-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]"
            >
              <div class="flex min-w-0 flex-col gap-1">
                <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <h3 class="min-w-0 text-sm font-medium [overflow-wrap:anywhere]">{{ p.title }}</h3>
                  <Badge variant="muted">{{ copy.editedElsewhere }}</Badge>
                  <Badge v-if="p.keys.length" variant="outline">{{ copy.storedKeys(p.keys.length) }}</Badge>
                </div>
                <p class="font-mono text-xs text-muted-foreground [overflow-wrap:anywhere]">{{ p.keyPattern }}</p>
                <p class="mt-0.5 max-w-[68ch] text-[13px] leading-5 text-pretty text-muted-foreground">{{ p.description }}</p>
              </div>
              <div v-if="p.link" class="lg:pt-0.5">
                <Button as-child variant="outline" size="sm">
                  <RouterLink :to="p.link.to">{{ p.link.label }} <ArrowRight /></RouterLink>
                </Button>
              </div>
            </li>
          </ul>
        </section>

        <details v-if="orphaned.length" class="surface group min-w-0 overflow-hidden">
          <summary class="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors hover:bg-state-hover sm:px-5 [&::-webkit-details-marker]:hidden">
            <ChevronRight class="size-4 shrink-0 text-muted-foreground transition-transform duration-150 group-open:rotate-90" aria-hidden="true" />
            {{ copy.unknownKeys(orphaned.length) }}
          </summary>
          <div class="border-t px-4 py-3 sm:px-5">
            <p class="text-sm text-muted-foreground">{{ copy.unknownHint }}</p>
            <ul class="mt-3 divide-y rounded-lg border">
              <li v-for="s in orphaned" :key="s.key" class="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center">
                <div class="min-w-0 flex-1">
                  <p class="font-mono text-sm font-medium [overflow-wrap:anywhere]">{{ s.key }}</p>
                  <p class="truncate font-mono text-xs text-muted-foreground">{{ preview(s.value) }}</p>
                  <p v-if="s.updatedBy" class="text-xs text-muted-foreground">{{ copy.changedBy(s.updatedBy) }} · <RelativeTime :value="s.updatedAt" /></p>
                  <FormError :error="errors[s.key]" />
                </div>
                <Badge v-if="s.locked" variant="muted">{{ t.admin.locked }}</Badge>
                <Button v-else variant="ghost" size="sm" :disabled="busyKey === s.key" :aria-label="`${copy.deleteKey} ${s.key}`" @click="remove(s)">
                  <Trash2 /> {{ copy.deleteKey }}
                </Button>
              </li>
            </ul>
          </div>
        </details>
      </div>
    </div>
  </div>
</template>
